import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useLanguage } from "@/components/LanguageProvider";

type DateLike = Date | string | number | null | undefined;

const toDate = (value: DateLike): Date | null => {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === "number") {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  if (typeof value === "string") {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  return null;
};

type FormatCurrencyOptions = {
  isMinorUnit?: boolean;
  formatOptions?: Intl.NumberFormatOptions;
};

type FormatNumberOptions = {
  formatOptions?: Intl.NumberFormatOptions;
};

export const useLocaleFormatter = () => {
  const { resolvedLanguage } = useLanguage();
  const { t } = useTranslation();

  const numberFormatter = useMemo(() => new Intl.NumberFormat(resolvedLanguage), [resolvedLanguage]);
  const percentFormatter = useMemo(
    () =>
      new Intl.NumberFormat(resolvedLanguage, {
        style: "percent",
        minimumFractionDigits: 0,
        maximumFractionDigits: 1,
      }),
    [resolvedLanguage],
  );
  const relativeTimeFormatter = useMemo(
    () =>
      new Intl.RelativeTimeFormat(resolvedLanguage, {
        numeric: "auto",
      }),
    [resolvedLanguage],
  );

  const formatNumber = useCallback(
    (value: number | null | undefined, options?: FormatNumberOptions) => {
      if (value === null || value === undefined || Number.isNaN(value)) {
        return "--";
      }
      if (options?.formatOptions) {
        return new Intl.NumberFormat(resolvedLanguage, options.formatOptions).format(value);
      }
      return numberFormatter.format(value);
    },
    [numberFormatter, resolvedLanguage],
  );

  const formatPercent = useCallback(
    (value: number | null | undefined, options?: Intl.NumberFormatOptions) => {
      if (value === null || value === undefined || Number.isNaN(value)) {
        return "--";
      }
      if (options) {
        return new Intl.NumberFormat(resolvedLanguage, options).format(value);
      }
      return percentFormatter.format(value);
    },
    [percentFormatter, resolvedLanguage],
  );

  const formatCurrency = useCallback(
    (value: number | null | undefined, currency = "USD", options: FormatCurrencyOptions = {}) => {
      if (value === null || value === undefined || Number.isNaN(value)) {
        return "--";
      }
      const amount = options.isMinorUnit ? value / 100 : value;
      const formatter = new Intl.NumberFormat(resolvedLanguage, {
        style: "currency",
        currency,
        currencyDisplay: "symbol",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        ...options.formatOptions,
      });
      return formatter.format(amount);
    },
    [resolvedLanguage],
  );

  const formatDate = useCallback(
    (value: DateLike, options: Intl.DateTimeFormatOptions = { dateStyle: "medium" }) => {
      const date = toDate(value);
      if (!date) {
        return "--";
      }
      return new Intl.DateTimeFormat(resolvedLanguage, options).format(date);
    },
    [resolvedLanguage],
  );

  const formatRelativeTime = useCallback(
    (value: DateLike) => {
      const date = toDate(value);
      if (!date) {
        return t("dates.justNow");
      }
      const now = Date.now();
      const diffMs = date.getTime() - now;
      const diffSeconds = Math.round(diffMs / 1000);
      const absSeconds = Math.abs(diffSeconds);

      if (absSeconds < 45) {
        return relativeTimeFormatter.format(diffSeconds, "second");
      }

      const diffMinutes = Math.round(diffSeconds / 60);
      const absMinutes = Math.abs(diffMinutes);
      if (absMinutes < 45) {
        return relativeTimeFormatter.format(diffMinutes, "minute");
      }

      const diffHours = Math.round(diffMinutes / 60);
      const absHours = Math.abs(diffHours);
      if (absHours < 36) {
        return relativeTimeFormatter.format(diffHours, "hour");
      }

      const diffDays = Math.round(diffHours / 24);
      const absDays = Math.abs(diffDays);
      if (absDays < 30) {
        return relativeTimeFormatter.format(diffDays, "day");
      }

      const diffMonths = Math.round(diffDays / 30);
      const absMonths = Math.abs(diffMonths);
      if (absMonths < 18) {
        return relativeTimeFormatter.format(diffMonths, "month");
      }

      const diffYears = Math.round(diffMonths / 12);
      return relativeTimeFormatter.format(diffYears, "year");
    },
    [relativeTimeFormatter, t],
  );

  return {
    language: resolvedLanguage,
    formatNumber,
    formatPercent,
    formatCurrency,
    formatDate,
    formatRelativeTime,
  };
};
